import { useEffect, useState } from "react";
import axios from "axios";
import { Target } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { usd } from "@/components/dash/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const pct = (v) => (v == null ? "—" : `${Math.round(v * 100)}%`);

const Stat = ({ label, s, testid }) => (
  <div className="rounded-lg border border-[#1A2338] bg-[#0F1A2E] p-3" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-widest font-num text-slate-400">{label}</div>
    <div className="font-num font-bold text-xl mt-1">{s ? `${s.hit_rate}%` : "—"}</div>
    <div className="text-[11px] text-slate-500">{s ? `Error score ${s.brier} (lower is better)` : "Waiting for results"}</div>
  </div>
);

export const Scoreboard = ({ tick }) => {
  const [d, setD] = useState(null);
  useEffect(() => {
    axios.get(`${API}/bot/scoreboard`).then((r) => setD(r.data)).catch(() => {});
  }, [tick]);
  if (!d) return null;
  return (
    <section className="card p-5 sm:p-6 fade-up" data-testid="scoreboard">
      <h2 className="font-head font-bold text-lg flex items-center gap-2"><Target className="w-5 h-5 text-[#22D3EE]" /> How accurate is the bot?</h2>
      <p className="text-xs text-slate-400 mt-1">
        3 minutes into every window the bot writes down its call. When the window ends we check who was right.
        "Right" = the side it gave over 50% actually won. "Official final" is Polymarket US's settlement price; "Our BRTI check" is our own average of the last 60 BRTI prices, so you can confirm the results match. The error score measures how close the % was (0 = perfect, 0.25 = coin flip).
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
        <Stat label="Bot (final call)" s={d.bot} testid="score-bot" />
        <Stat label="Bot math only" s={d.model_only} testid="score-model" />
        <Stat label="Polymarket crowd" s={d.market} testid="score-market" />
      </div>
      <p className="text-[11px] text-slate-500 mt-2" data-testid="score-count">{d.count} finished windows checked so far. Needs 50+ to mean much.</p>
      <div className="mt-4 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-[#1A2338] hover:bg-transparent">
              {["Window", "Bot said Up", "Crowd said Up", "Price to beat", "Official final", "Our BRTI check", "Winner", "Bot right?"].map((h) => (
                <TableHead key={h} className="text-[10px] uppercase tracking-widest font-num text-slate-500 whitespace-nowrap">{h}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {d.windows.map((w) => {
              const done = w.outcome === "Up" || w.outcome === "Down";
              const right = done && w.bot_up != null && (w.bot_up >= 0.5) === (w.outcome === "Up");
              return (
                <TableRow key={w.window_ts} className="border-[#1A2338] text-xs" data-testid="score-row">
                  <TableCell className="font-num whitespace-nowrap text-slate-400">{new Date(w.window_ts * 1000).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</TableCell>
                  <TableCell className="font-num">{pct(w.bot_up)}</TableCell>
                  <TableCell className="font-num">{pct(w.market_up)}</TableCell>
                  <TableCell className="font-num whitespace-nowrap">{usd(w.start)}</TableCell>
                  <TableCell className="font-num whitespace-nowrap" data-testid="score-official-final">{w.close ? usd(w.close) : "—"}</TableCell>
                  <TableCell className="font-num whitespace-nowrap" data-testid="score-our-check">
                    {w.our_check ? <>{usd(w.our_check)} {w.check_match ? <span className="text-[#34D399]">✓</span> : <span className="text-amber-400">!</span>}</> : "—"}
                  </TableCell>
                  <TableCell className={w.outcome === "Up" ? "text-[#34D399] font-semibold" : w.outcome === "Down" ? "text-[#F87171] font-semibold" : "text-slate-500"}>{done ? w.outcome : w.outcome === "unknown" ? "?" : "Live"}</TableCell>
                  <TableCell>{done && w.bot_up != null ? (right ? <span className="text-[#34D399]">Yes</span> : <span className="text-[#F87171]">No</span>) : "—"}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </section>
  );
};
